import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  loadPdfJs,
  PDFDocumentProxy,
  PDFPageProxy,
  PDF_WORKER_URL,
} from './pdfjs-loader';

export type PdfCanvasSrc =
  | string
  | Uint8Array
  | { data: Uint8Array }
  | null
  | undefined;

@Component({
    selector: 'app-pdf-canvas-viewer',
    templateUrl: './pdf-canvas-viewer.component.html',
    styleUrls: ['./pdf-canvas-viewer.component.css'],
    standalone: false
})
export class PdfCanvasViewerComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() src: PdfCanvasSrc;
  /** Multiplier on top of fit-to-width scale (1 = 100%). */
  @Input() zoom = 1;

  @ViewChild('pagesHost', { static: true }) pagesHost!: ElementRef<HTMLDivElement>;
  @ViewChild('scroll', { static: true }) scrollEl!: ElementRef<HTMLDivElement>;

  loading = false;
  error: string | null = null;
  readonly minZoom = 0.75;
  readonly maxZoom = 5;
  defaultZoom = 1;
  private pinchStartDistance = 0;
  private pinchStartZoom = 1;
  private pinchAnchor: { x: number; y: number } | null = null;

  private pdfDoc: PDFDocumentProxy | null = null;
  private renderToken = 0;
  private renderedZoom = 1;
  private resizeObserver: ResizeObserver | null = null;
  private lastRenderWidth = 0;
  /** Punto del documento (en px del render actual) que debe quedar bajo (viewX, viewY) tras el zoom. */
  private zoomAnchor: { viewX: number; viewY: number; docX: number; docY: number } | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['zoom']?.firstChange) {
      this.defaultZoom = this.clampZoom(this.zoom);
    }
    if (changes.src) {
      this.zoom = this.clampZoom(this.zoom);
      void this.reload();
      return;
    }
    if (changes.zoom) {
      this.zoom = this.clampZoom(this.zoom);
      void this.rerenderCurrentDocument();
    }
  }

  zoomOut(): void { this.setZoom(this.zoom - 0.25); }
  zoomIn(): void { this.setZoom(this.zoom + 0.25); }
  resetZoom(): void { this.setZoom(this.defaultZoom); }
  get zoomPercent(): number { return Math.round(this.zoom * 100); }

  onWheel(event: WheelEvent): void {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    this.setZoom(this.zoom + (event.deltaY < 0 ? 0.1 : -0.1), true, {
      x: event.clientX,
      y: event.clientY,
    });
  }

  onTouchStart(event: TouchEvent): void {
    if (event.touches.length === 2) {
      this.pinchStartDistance = this.touchDistance(event);
      this.pinchStartZoom = this.zoom;
      this.pinchAnchor = this.touchMidpoint(event);
    }
  }

  onTouchMove(event: TouchEvent): void {
    if (event.touches.length !== 2 || !this.pinchStartDistance) return;
    event.preventDefault();
    this.setZoom(this.pinchStartZoom * this.touchDistance(event) / this.pinchStartDistance, false);
  }

  onTouchEnd(): void {
    if (this.pinchStartDistance && this.pdfDoc && this.zoom !== this.renderedZoom) {
      this.captureZoomAnchor(this.pinchAnchor ?? undefined);
      void this.rerenderCurrentDocument();
    }
    this.pinchStartDistance = 0;
    this.pinchAnchor = null;
  }

  ngAfterViewInit(): void {
    const el = this.scrollEl?.nativeElement;
    if (!el || typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width < 32 || !this.pdfDoc) return;
      if (Math.abs(width - this.lastRenderWidth) < 8) return;
      this.lastRenderWidth = width;
      void this.rerenderCurrentDocument();
    });
    this.resizeObserver.observe(el);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.renderToken++;
    this.destroyDoc();
  }

  private async reload(): Promise<void> {
    const token = ++this.renderToken;
    this.error = null;
    this.zoomAnchor = null;
    this.destroyDoc();
    this.clearCanvases();

    if (!this.src) {
      this.loading = false;
      return;
    }

    this.loading = true;
    try {
      const data = this.resolveSource(this.src);
      if (!data) {
        this.loading = false;
        return;
      }

      const pdfjs = await loadPdfJs();
      if (!pdfjs.GlobalWorkerOptions.workerSrc) {
        pdfjs.GlobalWorkerOptions.workerSrc =
          (window as any).pdfWorkerSrc || PDF_WORKER_URL;
      }

      const pdf = await pdfjs.getDocument(data).promise;
      if (token !== this.renderToken) {
        await pdf.destroy();
        return;
      }

      this.pdfDoc = pdf;
      await this.renderAllPages(pdf, token);
    } catch (e) {
      if (token === this.renderToken) {
        console.error('[PdfCanvasViewer]', e);
        this.error = 'No se pudo cargar el PDF.';
      }
    } finally {
      if (token === this.renderToken) {
        this.loading = false;
      }
    }
  }

  private resolveSource(
    src: Exclude<PdfCanvasSrc, null | undefined>
  ): { url: string } | { data: Uint8Array } | null {
    if (typeof src === 'string') {
      return { url: src };
    }
    // pdf.js transfiere (detach) el buffer al worker: se entrega una copia
    // para que el origen siga siendo reutilizable en renders posteriores.
    if (src instanceof Uint8Array) {
      return { data: src.slice() };
    }
    if (
      src &&
      typeof src === 'object' &&
      (src as { data?: Uint8Array }).data instanceof Uint8Array
    ) {
      return { data: (src as { data: Uint8Array }).data.slice() };
    }
    return null;
  }

  private async renderAllPages(
    pdf: PDFDocumentProxy,
    token: number
  ): Promise<void> {
    const host = this.pagesHost?.nativeElement;
    if (!host) {
      return;
    }

    const containerWidth = this.measureRenderWidth();
    this.lastRenderWidth = containerWidth;
    const zoom = this.clampZoom(this.zoom || 1);
    const canvases: HTMLCanvasElement[] = [];

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      if (token !== this.renderToken) {
        return;
      }
      const page = await pdf.getPage(pageNum);
      if (token !== this.renderToken) {
        page.cleanup();
        return;
      }
      canvases.push(await this.renderPage(page, containerWidth, zoom));
      page.cleanup();
    }

    if (token !== this.renderToken) {
      return;
    }
    host.replaceChildren(...canvases);
    const previousZoom = this.renderedZoom;
    this.renderedZoom = zoom;
    this.restoreZoomAnchor(zoom / previousZoom);
  }

  private async renderPage(
    page: PDFPageProxy,
    containerWidth: number,
    zoom: number
  ): Promise<HTMLCanvasElement> {
    const baseViewport = page.getViewport({ scale: 1 });
    const fitScale = containerWidth / baseViewport.width;
    const viewport = page.getViewport({ scale: fitScale * zoom });

    const canvas = document.createElement('canvas');
    canvas.className = 'pdf-page-canvas';
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.style.width = `${Math.floor(viewport.width)}px`;
    canvas.style.height = 'auto';
    canvas.style.display = 'block';
    canvas.style.marginBottom = '8px';

    const context = canvas.getContext('2d');
    if (context) {
      await page.render({
        canvasContext: context,
        viewport,
      }).promise;
    }
    return canvas;
  }

  /** Guarda qué punto del documento está bajo `client` (o el centro visible) antes de cambiar el zoom. */
  private captureZoomAnchor(client?: { x: number; y: number }): void {
    if (this.zoomAnchor) return;
    const el = this.scrollEl?.nativeElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewX = client ? client.x - rect.left : el.clientWidth / 2;
    const viewY = client ? client.y - rect.top : el.clientHeight / 2;
    const hostOffset = this.hostOffsetInScroll(el);
    this.zoomAnchor = {
      viewX,
      viewY,
      docX: el.scrollLeft + viewX - hostOffset.x,
      docY: el.scrollTop + viewY - hostOffset.y,
    };
  }

  private restoreZoomAnchor(ratio: number): void {
    const anchor = this.zoomAnchor;
    this.zoomAnchor = null;
    const el = this.scrollEl?.nativeElement;
    if (!anchor || !el || !isFinite(ratio)) return;
    const hostOffset = this.hostOffsetInScroll(el);
    el.scrollLeft = anchor.docX * ratio + hostOffset.x - anchor.viewX;
    el.scrollTop = anchor.docY * ratio + hostOffset.y - anchor.viewY;
  }

  private hostOffsetInScroll(el: HTMLElement): { x: number; y: number } {
    const scrollRect = el.getBoundingClientRect();
    const hostRect = this.pagesHost.nativeElement.getBoundingClientRect();
    return {
      x: hostRect.left - scrollRect.left + el.scrollLeft,
      y: hostRect.top - scrollRect.top + el.scrollTop,
    };
  }

  private clearCanvases(): void {
    const host = this.pagesHost?.nativeElement;
    if (host) {
      host.innerHTML = '';
    }
  }

  private destroyDoc(): void {
    if (this.pdfDoc) {
      this.pdfDoc.destroy().catch(() => null);
      this.pdfDoc = null;
    }
  }

  private setZoom(value: number, rerender = true, anchor?: { x: number; y: number }): void {
    const nextZoom = this.clampZoom(value);
    if (nextZoom === this.zoom) return;
    this.zoom = nextZoom;
    if (rerender && this.pdfDoc) {
      this.captureZoomAnchor(anchor);
      void this.rerenderCurrentDocument();
    }
  }

  private async rerenderCurrentDocument(): Promise<void> {
    if (!this.pdfDoc) return;

    const token = ++this.renderToken;
    try {
      await this.renderAllPages(this.pdfDoc, token);
    } catch (e) {
      if (token === this.renderToken) {
        console.error('[PdfCanvasViewer]', e);
        this.zoomAnchor = null;
        this.error = 'No se pudo actualizar el zoom del PDF.';
      }
    }
  }

  private measureRenderWidth(): number {
    const el = this.scrollEl?.nativeElement;
    if (!el) {
      return 320;
    }
    const styles = getComputedStyle(el);
    const pad = parseFloat(styles.paddingLeft || '0') + parseFloat(styles.paddingRight || '0');
    return Math.max(el.clientWidth - pad, 1);
  }

  private clampZoom(value: number): number {
    return Math.min(this.maxZoom, Math.max(this.minZoom, value || 1));
  }

  private touchDistance(event: TouchEvent): number {
    const dx = event.touches[0].clientX - event.touches[1].clientX;
    const dy = event.touches[0].clientY - event.touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private touchMidpoint(event: TouchEvent): { x: number; y: number } {
    return {
      x: (event.touches[0].clientX + event.touches[1].clientX) / 2,
      y: (event.touches[0].clientY + event.touches[1].clientY) / 2,
    };
  }
}
